import React, { useState } from 'react';
import {
  Flame,
  Loader,
  LogOut,
  Menu,
  X,
  User,
  Shield,
  Sun,
  Moon,
} from 'lucide-react';
import arcEdLogo from '../assets/brand/ARC-Ed-Wordmark-Gold.svg';
import arcEdLogoBlack from '../assets/brand/ARC-Ed-Wordmark-Black.svg';

/*******************************************************************************
 * Helper Components                                                            *
 ******************************************************************************/

const LiveStatus = ({ lastUpdated = new Date() }) => (
  <div className="hidden lg:flex items-center gap-3 text-xs">
    <div className="flex items-center gap-2">
      <span className="pulse-dot" aria-hidden="true" />
      <span className="font-semibold uppercase tracking-[0.16em] text-arc-accent-ink">
        Live
      </span>
    </div>
    <span className="text-arc-ink-4">|</span>
    <span className="tnum text-arc-ink-3">
      Updated {lastUpdated.toLocaleDateString()}
    </span>
  </div>
);

/* TEMPORARY (Design Phase 1): light mode isn't ready across all tabs, so the theme
   toggle is hidden and the app is locked to dark (see useTheme LOCK_DARK). Set this
   back to true to restore the toggle once Phase 1 lands. The ThemeToggle component and
   its wiring are left intact so re-enabling is a one-line flip. */
const SHOW_THEME_TOGGLE = false;

const ThemeToggle = ({ theme, onToggle, className = '' }) => (
  <button
    type="button"
    onClick={onToggle}
    aria-label={theme === 'dark' ? 'Switch to light theme' : 'Switch to dark theme'}
    className={`btn-tactile p-2 rounded-arc-ctl text-arc-ink-3 hover:text-arc-ink hover:bg-arc-paper-2 transition ${className}`}
  >
    {theme === 'dark' ? <Sun size={18} /> : <Moon size={18} />}
  </button>
);

const BrandLogo = () => (
  <div className="flex items-center gap-3 sm:gap-4">
    {/* Company Logo */}
    <img
      src={arcEdLogo}
      alt="ARC Ed"
      className="h-8 sm:h-10 w-auto object-contain opacity-95"
    />

    {/* Divider */}
    <div className="h-6 sm:h-8 w-px bg-arc-line hidden xs:block" />

    {/* FIRE Tracker mark — gold gradient rounded square (spec §5) */}
    <div className="flex items-center gap-2 sm:gap-3">
      <div className="relative">
        <div className="arc-grad-bg w-8 h-8 sm:w-10 sm:h-10 rounded-arc-ctl flex items-center justify-center shadow-arc-sm">
          <Flame className="w-4 h-4 sm:w-5 sm:h-5 text-[#0B0B0D]" strokeWidth={2.5} />
        </div>
      </div>

      <div className="flex flex-col leading-none">
        <h1 className="font-semibold tracking-[-0.02em] text-base sm:text-xl text-arc-ink">
          <span className="hidden xs:inline">ARC</span>
          <span className="text-arc-accent-ink">FIRE</span>
          <span>Tracker</span>
        </h1>
        <p className="text-[11px] font-semibold tracking-[0.04em] uppercase text-arc-ink-4 mt-1.5 hidden sm:block">
          What gets measured gets manifested
        </p>
      </div>
    </div>
  </div>
);

const VerifiedPill = ({ user }) => {
  const verified = user?.emailVerified;
  return (
    <span
      className={`inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.12em] ${
        verified ? 'text-arc-accent-ink' : 'text-arc-ink-3'
      }`}
    >
      <Shield size={10} />
      {verified ? 'Verified' : 'Unverified'}
    </span>
  );
};

const MobileMenu = ({
  isOpen,
  onClose,
  user,
  onLogout,
  loggingOut,
  lastUpdated,
  theme,
  onToggleTheme,
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 lg:hidden">
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-[rgba(0,0,0,0.6)] backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Menu */}
      <div className="fixed top-0 right-0 h-full w-80 max-w-full bg-arc-card border-l border-arc-line shadow-arc-lg">
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-5 border-b border-arc-line">
            <span className="text-[11px] font-semibold tracking-[0.04em] uppercase text-arc-ink-4">
              Account
            </span>
            <div className="flex items-center gap-1">
              {SHOW_THEME_TOGGLE && <ThemeToggle theme={theme} onToggle={onToggleTheme} />}
              <button
                onClick={onClose}
                className="btn-tactile p-2 rounded-arc-ctl text-arc-ink-3 hover:text-arc-ink hover:bg-arc-paper-2"
                aria-label="Close menu"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 p-5 space-y-6">
            {/* User Info */}
            <div className="flex items-center gap-3">
              <div className="arc-grad-bg w-12 h-12 rounded-full flex items-center justify-center">
                <User className="w-5 h-5 text-[#0B0B0D]" strokeWidth={2.5} />
              </div>
              <div className="min-w-0">
                <p className="font-medium text-arc-ink truncate">
                  {user?.displayName || user?.email || 'User'}
                </p>
                <div className="mt-1">
                  <VerifiedPill user={user} />
                </div>
              </div>
            </div>

            {/* Status Info */}
            <div className="space-y-3 pt-4 border-t border-arc-line">
              <div className="flex items-center gap-2">
                <span className="pulse-dot" aria-hidden="true" />
                <span className="text-xs font-semibold uppercase tracking-[0.16em] text-arc-accent-ink">
                  Live data active
                </span>
              </div>
              <div className="tnum text-xs text-arc-ink-3">
                Last updated {lastUpdated.toLocaleDateString()}
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="p-5 border-t border-arc-line">
            <button
              onClick={onLogout}
              disabled={loggingOut}
              className="btn-tactile w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-semibold rounded-arc-ctl bg-arc-card text-arc-ink ring-1 ring-arc-line hover:bg-arc-paper-2 disabled:opacity-50"
            >
              {loggingOut ? (
                <Loader className="animate-spin" size={16} />
              ) : (
                <LogOut size={16} />
              )}
              {loggingOut ? 'Signing out…' : 'Sign Out'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/*******************************************************************************
 * Main Header Component                                                        *
 ******************************************************************************/

const Header = ({ user, onLogout, lastUpdated, theme = 'dark', onToggleTheme }) => {
  const [loggingOut, setLoggingOut] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    setLoggingOut(true);
    setMobileMenuOpen(false);
    await onLogout?.();
    setLoggingOut(false);
  };

  return (
    <>
      {/* Signature 3px gold top band (spec §5) */}
      <div className="arc-grad-bg h-[3px] w-full" aria-hidden="true" />

      <header className="arc-blur-bar sticky top-0 z-40 border-b border-arc-line">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16 sm:h-18 py-3">
            {/* Brand */}
            <div className="flex-1 min-w-0">
              <BrandLogo />
            </div>

            {/* Live status (hidden on mobile/tablet) */}
            <div className="flex-shrink-0 mx-4">
              <LiveStatus lastUpdated={lastUpdated} />
            </div>

            {/* Desktop user + theme toggle + logout */}
            <div className="hidden lg:flex items-center gap-4 flex-shrink-0">
              <div className="text-right">
                <p className="text-sm font-medium text-arc-ink">
                  {user?.displayName || user?.email || 'User'}
                </p>
                <div className="mt-0.5">
                  <VerifiedPill user={user} />
                </div>
              </div>

              {SHOW_THEME_TOGGLE && <ThemeToggle theme={theme} onToggle={onToggleTheme} />}

              <button
                onClick={handleLogout}
                disabled={loggingOut}
                className="btn-tactile flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-arc-ctl bg-arc-card text-arc-ink ring-1 ring-arc-line hover:bg-arc-paper-2 disabled:opacity-50"
              >
                {loggingOut ? (
                  <Loader className="animate-spin" size={16} />
                ) : (
                  <LogOut size={16} />
                )}
                <span className="hidden xl:inline">
                  {loggingOut ? 'Signing out…' : 'Sign Out'}
                </span>
              </button>
            </div>

            {/* Mobile menu button */}
            <div className="flex lg:hidden items-center gap-2">
              {SHOW_THEME_TOGGLE && <ThemeToggle theme={theme} onToggle={onToggleTheme} />}
              <div className="arc-grad-bg w-8 h-8 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-[#0B0B0D]" strokeWidth={2.5} />
              </div>
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="btn-tactile p-2 rounded-arc-ctl text-arc-ink-3 hover:text-arc-ink hover:bg-arc-paper-2"
                aria-label="Open menu"
              >
                <Menu size={20} />
              </button>
            </div>
          </div>
        </div>
      </header>

      <MobileMenu
        isOpen={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        user={user}
        onLogout={handleLogout}
        loggingOut={loggingOut}
        lastUpdated={lastUpdated}
        theme={theme}
        onToggleTheme={onToggleTheme}
      />
    </>
  );
};

/*******************************************************************************
 * Optional standalone logo variants                                            *
 ******************************************************************************/

export const LogoVariants = {
  CompactLogo: () => (
    <div className="flex items-center gap-3">
      <img src={arcEdLogo} alt="ARC Ed" className="h-6 sm:h-8 w-auto object-contain" />
      <div className="h-4 sm:h-6 w-px bg-arc-line" />
      <div className="flex items-center gap-2">
        <div className="arc-grad-bg w-6 h-6 rounded-arc-sm flex items-center justify-center">
          <Flame className="w-3 h-3 text-[#0B0B0D]" strokeWidth={2.5} />
        </div>
        <span className="font-semibold text-arc-ink">
          <span className="text-arc-accent-ink">FIRE</span>Tracker
        </span>
      </div>
    </div>
  ),

  PrintLogo: () => (
    <div className="flex items-center justify-center gap-4">
      <img src={arcEdLogoBlack} alt="ARC Ed" className="h-12 w-auto object-contain print:h-10" />
      <div className="h-10 w-px bg-arc-line" />
      <BrandLogo />
    </div>
  ),
};

export default Header;
